import { useEffect, useState } from 'react';
import webrtc from '@/services/webrtc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Phone, PhoneOff, PhoneIncoming, X } from 'lucide-react';
import { toast } from 'sonner';

const readOccupancy = () => {
  const status = webrtc.getStatus();
  return {
    hasActiveCall: !!status.hasActiveCall,
    campaignActive: !!status.campaignActive,
    hasIncomingCall: !!status.hasIncomingCall
  };
};

const IncomingCallModal = () => {
  const [incoming, setIncoming] = useState(null);
  const [occupancy, setOccupancy] = useState({
    hasActiveCall: false,
    campaignActive: false
  });
  const [answering, setAnswering] = useState(false);

  const refreshOccupancy = () => {
    setOccupancy(readOccupancy());
  };

  useEffect(() => {
    const onIncoming = (info) => {
      setAnswering(false);
      setIncoming(info);
      setOccupancy(readOccupancy());
    };

    const onEnded = () => {
      setIncoming(null);
      setAnswering(false);
    };

    const onOccupancy = () => {
      const status = readOccupancy();
      setOccupancy(status);
      if (!status.hasIncomingCall && !webrtc.incomingSession) {
        setIncoming(null);
        setAnswering(false);
      }
    };

    webrtc.onIncomingCall = onIncoming;
    webrtc.onIncomingCallEnded = onEnded;
    webrtc.onOccupancyChange = onOccupancy;

    return () => {
      if (webrtc.onIncomingCall === onIncoming) webrtc.onIncomingCall = null;
      if (webrtc.onIncomingCallEnded === onEnded) webrtc.onIncomingCallEnded = null;
      if (webrtc.onOccupancyChange === onOccupancy) webrtc.onOccupancyChange = null;
    };
  }, []);

  useEffect(() => {
    if (!incoming) return undefined;
    refreshOccupancy();
    const timer = setInterval(refreshOccupancy, 400);
    return () => clearInterval(timer);
  }, [incoming]);

  if (!incoming) {
    return null;
  }

  const busy = occupancy.hasActiveCall || occupancy.campaignActive;
  const caller = incoming.displayName && incoming.displayName !== incoming.phone
    ? `${incoming.displayName} (${incoming.phone})`
    : (incoming.phone || 'Número desconhecido');

  let busyMessage = '';
  if (occupancy.hasActiveCall && occupancy.campaignActive) {
    busyMessage = 'Você já está em uma chamada e há uma campanha em andamento. Desligue a chamada atual e encerre a campanha para atender.';
  } else if (occupancy.hasActiveCall) {
    busyMessage = 'Você já está em uma chamada. Desligue a chamada atual para poder atender.';
  } else if (occupancy.campaignActive) {
    busyMessage = 'Há uma campanha de discagem em andamento. Encerre a campanha para poder atender.';
  }

  const handleAnswer = async () => {
    const live = readOccupancy();
    if (live.hasActiveCall || live.campaignActive) {
      setOccupancy(live);
      return;
    }
    setAnswering(true);
    const result = await webrtc.answerIncomingCall();
    if (!result.success) {
      toast.error(result.error || 'Não foi possível atender');
      setAnswering(false);
      refreshOccupancy();
      return;
    }
    setIncoming(null);
    setAnswering(false);
    toast.success('Chamada atendida');
  };

  const handleReject = () => {
    webrtc.rejectIncomingCall();
    setIncoming(null);
    setAnswering(false);
    toast.info('Chamada recusada');
  };

  const handleHangupCurrent = () => {
    const result = webrtc.hangup();
    if (!result.success) {
      toast.error(result.error || 'Não foi possível desligar a chamada atual');
    }
    refreshOccupancy();
    setTimeout(refreshOccupancy, 300);
  };

  const handleStopCampaign = () => {
    webrtc.requestStopCampaign();
    toast.info('Campanha encerrada');
    refreshOccupancy();
    setTimeout(refreshOccupancy, 300);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
      <Card className="w-full max-w-md border-2 border-teal-400 shadow-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <PhoneIncoming className="text-teal-600 animate-pulse" size={22} />
            Chamada recebida
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
            <p className="text-sm text-teal-800 mb-1">De</p>
            <p className="text-2xl font-semibold text-slate-900 break-all">{caller}</p>
          </div>

          {busy ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {busyMessage}
            </p>
          ) : (
            <p className="text-sm text-slate-600 text-center">
              Deseja atender esta chamada?
            </p>
          )}

          <div className="flex flex-col gap-2">
            {occupancy.hasActiveCall && (
              <Button
                onClick={handleHangupCurrent}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <PhoneOff size={16} className="mr-2" />
                Desligar chamada atual
              </Button>
            )}
            {occupancy.campaignActive && (
              <Button
                onClick={handleStopCampaign}
                variant="outline"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
              >
                <X size={16} className="mr-2" />
                Encerrar campanha
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button
                onClick={handleReject}
                variant="outline"
                className="h-12"
              >
                Recusar
              </Button>
              <Button
                onClick={handleAnswer}
                disabled={busy || answering}
                className="h-12 bg-teal-600 hover:bg-teal-700"
              >
                <Phone size={16} className="mr-2" />
                {answering ? 'Atendendo...' : 'Atender'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IncomingCallModal;
